import type { JSX } from 'react'
import type { PlaceholderType } from '@cogo/types-v2'
import { ImageIcon, BarChart3 } from 'lucide-react'

interface PlaceholderRendererProps {
  type: PlaceholderType
  placeholderCount?: number
}

export function PlaceholderRenderer({
  type,
  placeholderCount = 3,
}: PlaceholderRendererProps): JSX.Element | null {
  switch (type) {
    case 'list':
      return (
        <div className="flex gap-3 overflow-hidden" data-testid="placeholder-list">
          {Array.from({ length: placeholderCount }, (_, i) => (
            <div
              key={i}
              data-testid="placeholder-list-item"
              className="h-[100px] w-[80px] shrink-0 animate-pulse rounded-lg bg-muted"
            />
          ))}
        </div>
      )

    case 'image':
      return (
        <div
          data-testid="placeholder-image"
          className="flex h-[150px] w-[200px] items-center justify-center rounded-lg bg-muted"
        >
          <ImageIcon className="h-8 w-8 text-muted-foreground" />
        </div>
      )

    case 'text': {
      const widths = ['100%', '80%', '60%']
      return (
        <div className="flex flex-col gap-2" data-testid="placeholder-text">
          {Array.from({ length: placeholderCount }, (_, i) => (
            <div
              key={i}
              data-testid="placeholder-text-line"
              className="h-4 animate-pulse rounded bg-muted"
              style={{ width: widths[i % widths.length] }}
            />
          ))}
        </div>
      )
    }

    case 'object':
      return (
        <div className="flex flex-col gap-2" data-testid="placeholder-object">
          {Array.from({ length: placeholderCount }, (_, i) => (
            <div
              key={i}
              data-testid="placeholder-object-field"
              className="flex items-center gap-2"
            >
              <div className="h-4 w-16 animate-pulse rounded bg-muted" />
              <div className="h-4 flex-1 animate-pulse rounded bg-muted" />
            </div>
          ))}
        </div>
      )

    case 'chart':
      return (
        <div
          data-testid="placeholder-chart"
          className="flex h-[160px] w-full items-center justify-center rounded-lg border border-dashed border-muted-foreground/30"
        >
          <BarChart3 className="h-8 w-8 text-muted-foreground" />
        </div>
      )

    default:
      return null
  }
}
